import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getNotableEvents, onLocalEvent } from "../lib/api";
import { subscribeLive } from "../lib/liveRealtime";
import { formatRelativeEventTime } from "../lib/notableEventsDisplay";

export function NotableEventsMarquee({
  initialEvents = [],
  fetchEvents = getNotableEvents,
  pollMs = 60_000
}) {
  const [events, setEvents] = useState(() => normalizeEvents(initialEvents));

  useEffect(() => {
    if (!fetchEvents) return undefined;
    let cancelled = false;

    async function refresh() {
      const nextEvents = await fetchEvents().catch(() => []);
      if (!cancelled) {
        setEvents(normalizeEvents(nextEvents));
      }
    }

    refresh();
    const unsubscribe = onLocalEvent((event) => {
      if (event?.type === "market_created" || event?.type === "market_updated" || event?.type === "leaderboards_updated") {
        refresh();
      }
    });
    const unsubscribeLive = subscribeLive({
      topics: ["notable-events"],
      onNotableEventsUpdate: refresh
    });
    const intervalId = pollMs > 0 ? window.setInterval(refresh, pollMs) : 0;

    return () => {
      cancelled = true;
      unsubscribe();
      unsubscribeLive();
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [fetchEvents, pollMs]);

  const visibleEvents = useMemo(() => normalizeEvents(events), [events]);
  if (!visibleEvents.length) {
    return <div className="notable-events-rail notable-events-rail-empty" aria-hidden="true" data-cls-measure="notable-events-rail" />;
  }

  return (
    <div className="notable-events-rail" aria-label="Recent notable events" data-cls-measure="notable-events-rail">
      <div className="notable-events-mask">
        <div className="notable-events-track">
          <EventLine events={visibleEvents} interactive />
        </div>
      </div>
    </div>
  );
}

function EventLine({ events, interactive = false, ...props }) {
  return (
    <div className="notable-events-line" {...props}>
      {events.map((event, index) => (
        <span className="notable-events-item" key={`${event.id}-${index}`}>
          <span className="notable-events-time">{formatRelativeEventTime(event.liveAt || event.occurredAt)}</span>
          {interactive && event.href ? (
            <Link className="notable-events-link" to={event.href}>
              {event.message}
            </Link>
          ) : (
            <span className={event.href ? "notable-events-link-copy" : "notable-events-copy"}>{event.message}</span>
          )}
          {index < events.length - 1 ? <span className="notable-events-separator">/</span> : null}
        </span>
      ))}
    </div>
  );
}

function normalizeEvents(events) {
  if (!Array.isArray(events)) return [];
  return events
    .filter((event) => event && event.visible !== false && event.message)
    .map((event) => ({
      id: String(event.id || `${event.message}-${event.occurredAt || ""}`),
      message: String(event.message || "").replace(/\s+/g, " ").trim(),
      href: normalizeHref(event.href),
      occurredAt: event.occurredAt || event.createdAt || new Date().toISOString(),
      liveAt: event.liveAt || event.occurredAt || event.createdAt || new Date().toISOString(),
      visible: event.visible !== false
    }))
    .filter((event) => event.message);
}

function normalizeHref(value) {
  const href = String(value || "").trim();
  return href.startsWith("/market/") ? href : "";
}
